import PropTypes from 'prop-types';

import React from 'react';
import { Link } from 'react-router';
import _ from 'lodash';
import { Flex, Box } from 'reflexbox';
import {
  Badge,
  Panel,
  PanelHeader,
  Text,
  Space,
  Divider,
  Dropdown,
  DropdownMenu,
  NavItem,
  Tooltip,
  Heading,
} from 'rebass';
import relativeDate from 'relative-date';
import { format } from 'date-fns';

import {
  fightStyles,
  getFoods,
  getFlasks,
  getPotions,
  getAugments,
  expansionOptions,
} from 'shared/simcOptions';
import { humanTimeDisplay } from 'shared/time';

import config from 'frontend/config';
import { dwarn } from 'frontend/util';
import {
  Icon,
  If,
  InfoTooltip,
  WowIcon,
} from 'frontend/components';

const hasGet = (obj, prop, defaultValue) => (_.has(obj, prop) ? _.get(obj, prop) : defaultValue);

class FileMenu extends React.Component {
  state = {
    open: false,
  };

  static propTypes = {
    baseUrl: PropTypes.string.isRequired,
    hasFullJson: PropTypes.bool,
    hasCsv: PropTypes.bool,
    hasOutput: PropTypes.bool,
  };

  static defaultProps = {
    hasFullJson: false,
    hasCsv: false,
    hasOutput: false,
  };

  render() {
    const { baseUrl, hasFullJson, hasCsv, hasOutput } = this.props;

    const files = [
      'data.json',
    ];
    if (hasFullJson) {
      files.push('data.full.json');
    }
    if (hasCsv) {
      files.push('data.csv');
    }
    files.push('input.txt');
    if (hasOutput) {
      files.push('output.txt');
    }

    return (
      <Dropdown>
        <NavItem
          p={0}
          title="More Options"
          onClick={() => this.setState(prev => ({ ...prev, open: !prev.open }))}
        >
          <Text bold size={18}>
            &#8943;
          </Text>
        </NavItem>
        <DropdownMenu
          onDismiss={() => this.setState({ open: false })}
          open={this.state.open}
          right
          style={{ width: 120 }}
        >
          {_.map(files, file => (
            <Box pl={1} my={1} key={file}>
              <Text small>
                <a
                  href={`${baseUrl}/${file}`}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  onClick={() => this.setState({ open: false })}
                >
                  {file}
                </a>
              </Text>
            </Box>
          ))}
        </DropdownMenu>
      </Dropdown>
    );
  }
}

const SimDetails = ({ simId, report, errorDetails, expertMode, unsupportedFightStyle }) => {
  const { sim, simbot } = report;

  const gitRevision = report.git_revision || report.git_rev;
  const simcGithubUrl = `https://github.com/simulationcraft/simc/commits/${gitRevision}`;
  const dataBaseUrl = `https://www.raidbots.com/reports/${simId}`;

  const fightStyleInfo = _.find(
    fightStyles,
    { value: _.get(simbot, 'meta.fightStyle') || _.get(sim, 'options.fight_style') || sim.fight_style },
  );
  const overrides = _.get(fightStyleInfo, 'overrides', {});

  const fightLength = hasGet(overrides, 'fightLength', _.get(sim, 'options.max_time') || _.get(sim, 'max_time'));
  const fightLengthVary = sim?.options?.vary_combat_length ?? 0;
  const fightLengthMax = fightLength * (1 + fightLengthVary);
  const fightLengthMin = fightLength * (1 - fightLengthVary);

  const enemyCount = hasGet(overrides, 'enemyCount', _.get(simbot, 'meta.enemyCount', 1));

  const smartSim = _.get(simbot, 'smart');
  const iterations = Number(_.get(sim, 'options.iterations') || sim.iterations).toLocaleString();

  // add in the base actor
  const actors = simbot.numProfilesets ? (1 + simbot.numProfilesets).toLocaleString() : undefined;

  const reportDate = simbot.date ? new Date(simbot.date) : undefined;
  const skippedHtml = simbot.skippedHtml || false;

  const simType = _.get(simbot, 'meta.type');
  const isAdvanced = simType === 'advanced';

  const duration = _.round(_.get(simbot, 'flightmaster.duration') / 1000, 0);

  const primaryActor = _.first(sim.players);

  // search buffs for flask used
  const flaskNames = new Set(_.filter(_.map(getFlasks(), 'value')));
  const potionNames = new Set(_.filter(_.map(getPotions(), 'value')));
  const foodNames = new Set(_.filter(_.map(getFoods(), 'value')));
  const augmentationNames = new Set(_.filter(_.map(getAugments(), 'value')));
  const allBuffs = _.union(primaryActor.buffs_constant, primaryActor.buffs);

  const findNameInToken = name => opt => opt.value?.includes(name);

  const flaskName = primaryActor.flask || _.get(_.find(allBuffs, buff => {
    return flaskNames.has(buff.name);
  }), 'name');
  const flaskOption = _.find(getFlasks(), findNameInToken(flaskName));

  const potionName = primaryActor.potion || _.get(_.find(allBuffs, buff => {
    return potionNames.has(buff.name);
  }), 'name');
  const potionOption = _.find(getPotions(), findNameInToken(potionName));

  const foodName = primaryActor.food || _.get(_.find(allBuffs, buff => {
    return foodNames.has(buff.name);
  }), 'name');
  const foodOption = _.find(getFoods(), findNameInToken(foodName));

  const augmentationName = primaryActor.augmentation || _.get(_.find(allBuffs, buff => {
    return augmentationNames.has(buff.name);
  }), 'name');
  const augmentationOption = _.find(getAugments(), findNameInToken(augmentationName));

  const data = {
    fightStyle: _.get(fightStyleInfo, 'name'),
    fightLength: humanTimeDisplay(fightLength, { round: false }),
    enemyCount,
    customApl: Boolean(_.get(simbot, 'meta.rawFormData.apl')),
    consumables: [{
      name: 'Potion',
      value: Boolean(potionOption && potionOption.value !== 'disabled'),
      icon: {
        type: 'item',
        id: _.get(potionOption, 'itemId'),
        default_icon: 'inv_potion_06',
      },
    }, {
      name: 'Flask',
      value: Boolean(flaskOption && flaskOption.value !== 'disabled'),
      icon: {
        type: 'item',
        id: _.get(flaskOption, 'itemId'),
        default_icon: 'trade_alchemy_dpotion_c26',
      },
    }, {
      name: 'Food',
      value: Boolean(foodOption && foodOption.value !== 'disabled'),
      icon: {
        type: 'item',
        id: _.get(foodOption, 'itemId'),
        default_icon: 'spell_misc_food',
      },
    }, {
      name: 'Augment Rune',
      value: Boolean(augmentationOption && augmentationOption.value !== 'disabled'),
      icon: {
        type: 'item',
        id: _.get(augmentationOption, 'itemId'),
        default_icon: 'inv_legendarygem_01',
      },
    }],
    buffs: [{
      name: 'Bloodlust',
      value: Boolean(hasGet(overrides, 'bloodlust', _.get(simbot, 'meta.bloodlust', false))),
      icon: {
        id: 2825,
        type: 'spell',
      },
    }, {
      name: 'Arcane Intellect',
      value: Boolean(hasGet(overrides, 'arcaneIntellect', _.get(simbot, 'meta.arcaneIntellect', false))),
      icon: {
        id: 1459,
        type: 'spell',
      },
    }, {
      name: 'Power Word: Fortitude',
      value: Boolean(hasGet(overrides, 'fortitude', _.get(simbot, 'meta.fortitude', false))),
      icon: {
        type: 'spell',
        id: 21562,
      },
    }, {
      name: 'Mark of the Wild',
      value: Boolean(hasGet(overrides, 'markOfTheWild', _.get(simbot, 'meta.markOfTheWild', false))),
      icon: {
        type: 'spell',
        id: 1126,
      },
    }, {
      name: 'Battle Shout',
      value: Boolean(hasGet(overrides, 'battleShout', _.get(simbot, 'meta.battleShout', false))),
      icon: {
        type: 'spell',
        id: 6673,
      },
    }, {
      name: 'Mystic Touch',
      value: Boolean(hasGet(overrides, 'mysticTouch', _.get(simbot, 'meta.mysticTouch', false))),
      icon: {
        type: 'spell',
        id: 8647,
      },
    }, {
      name: 'Chaos Brand',
      value: Boolean(hasGet(overrides, 'chaosBrand', _.get(simbot, 'meta.chaosBrand', false))),
      icon: {
        type: 'spell',
        id: 1490,
      },
    }, {
      name: 'Hunter\'s Mark',
      value: Boolean(hasGet(overrides, 'huntersMark', _.get(simbot, 'meta.huntersMark', false))),
      icon: {
        type: 'spell',
        id: 257284,
      },
    }, {
      name: 'Skyfury',
      value: Boolean(hasGet(overrides, 'skyfury', _.get(simbot, 'meta.skyfury', false))),
      icon: {
        type: 'spell',
        id: 462854,
      },
    }, {
      name: 'Power Infusion',
      value: Boolean(hasGet(overrides, 'powerInfusion', _.get(simbot, 'meta.powerInfusion', false))),
      icon: {
        type: 'spell',
        id: 10060,
      },
    }],
  };

  const preOptions = [{
    key: 'Sim Run',
    value: reportDate ? <Text small title={reportDate}>{relativeDate(reportDate)}</Text> : undefined,
  }, {
    key: 'Margin of Error',
    value: `~${_.round(errorDetails.error)} DPS (${_.round(100 * errorDetails.percent, 2)}%)`,
  }, {
    key: 'Iterations',
    value: smartSim ? `${iterations} (Smart Sim)` : iterations,
    description: 'Number of simulated fights run',
  }, {
    key: 'Actors',
    value: actors,
  }];

  const buildDate = new Date(`${report.build_date} ${report.build_time} UTC`);

  const iconHeight = 14;

  const postOptions = [{
    key: 'Processing Time',
    value: duration ? humanTimeDisplay(duration, { compact: true, round: false }) : undefined,
  }, {
    key: 'SimC Version',
    value: (
      <Flex align="center">
        <If
          condition={_.includes(['latest', 'canary'], simbot.simcVersion)}
          then={() => (
            <Box mr={1} style={{ height: iconHeight, lineHeight: 1 }}>
              <Tooltip
                title="Latest build can change frequently. Double check the git hash / build date when comparing sims!"
              >
                <Icon
                  name="warning"
                  color={config.colors.warning}
                  size={iconHeight}
                />
              </Tooltip>
            </Box>
          )}
        />
        <Text small>
          {_.capitalize(simbot.simcVersion)}
        </Text>
      </Flex>
    ),
  }, {
    key: 'SimC Build Date',
    value: format(buildDate, 'MMM d, h:mm aaa'),
  }, {
    key: 'SimC Git Hash',
    value: (<Text small><a href={simcGithubUrl} rel="noopener noreferrer" target="_blank">{gitRevision}</a></Text>),
  }];

  let options = [];

  if (!isAdvanced && !expertMode) {
    _.each(expansionOptions, option => {
      if (!option.archived) {
        const { key } = option;

        let value = _.get(simbot.meta, key);
        const override = overrides[key];
        if (override !== undefined) {
          value = override;
        }

        // Do not show a row if the value is the default
        if (value === option.default) {
          return;
        }

        let optionName;
        switch (option.type) {
          case 'bool':
            optionName = value ? 'Enabled' : 'Disabled';
            break;
          case 'string': {
            const optionValue = _.find(option.values, { value }) || _.find(option.values, { value: option.default });
            optionName = optionValue.name || optionValue.value;
            // force empty into a 0 string
            if (!optionName) {
              optionName = '';
            }
            break;
          }
          case 'int': {
            const optionValue = _.find(option.values, { value }) || _.find(option.values, { value: option.default });
            optionName = optionValue.name || optionValue.value;
            // force empty into a 0 string
            if (!optionName) {
              optionName = '0';
            }
            break;
          }
          default:
            dwarn(`Unknown expansion option type ${option.type} for ${key}`);
            break;
        }

        options.push({
          key: option.name,
          value: optionName,
        });
      }
    });

    options = [...options, {
      key: 'Bleeding',
      value: !hasGet(overrides, 'bleeding', _.get(simbot, 'meta.bleeding', false)) ? 'Disabled' : null,
    }];
  }

  const simcSettings = [
    ...preOptions,
    ...postOptions,
    ...options,
  ];

  let title = data.fightStyle;

  if (simType === 'advanced') {
    title = 'Advanced';
  } else if (expertMode) {
    title = 'Expert Mode Sim';
  }

  return (
    <Panel theme="primary" p={2} pb={1} id="simulationDetails">
      <PanelHeader p={1} mb={1}>
        Simulation Details
      </PanelHeader>
      <Flex align="center" mt={0}>
        <If
          condition={unsupportedFightStyle}
          then={() => (
            <Box mr={1} style={{ lineHeight: 1 }}>
              <Tooltip
                title="Unreliable fight style for this spec!"
              >
                <Icon
                  name="warning"
                  color={config.colors.error}
                />
              </Tooltip>
            </Box>
          )}
        />
        <Heading level={5}>
          {title}
        </Heading>
      </Flex>
      <Flex align="center">
        <Flex align="center">
          <Text size={13} bold>
            {data.fightLength}
          </Text>
          <span style={{ marginTop: -4, marginLeft: 4 }}>
            <InfoTooltip>
              By default, SimC varies fight length for each iteration to avoid overly specific results.  <br />
              In this sim, fight length varies from {fightLengthMin} to {fightLengthMax} seconds.
            </InfoTooltip>
          </span>
        </Flex>
        <If
          condition={!expertMode}
          then={() => (
            <Text size={13} ml={2}>{enemyCount} boss target{enemyCount !== 1 && 's'}</Text>
          )}
        />
      </Flex>
      <If
        condition={!expertMode}
        then={() => (
          <Flex justify="space-between">
            {_.map(data.consumables, consumable => {
              let icon = (
                <WowIcon
                  size="small"
                  {...consumable.icon}
                  icon={!consumable.value ? consumable.icon.default_icon : undefined}
                  dull={!consumable.value}
                  border="#ffffff66 1px solid"
                  noLink
                />
              );
              if (!consumable.value) {
                icon = (
                  <Tooltip title={`${consumable.name} not used`}>
                    {icon}
                  </Tooltip>
                );
              }
              return (
                <Box mt={1} style={{ height: 18 }} key={consumable.name}>
                  {icon}
                </Box>
              );
            })}
            {_.map(data.buffs, buff => (
              <Box mt={1} style={{ height: 18 }} key={buff.name}>
                <WowIcon
                  size="small"
                  {...buff.icon}
                  dull={!buff.value}
                  noLink
                  border="#ffffff66 1px solid"
                />
              </Box>
            ))}
          </Flex>
        )}
      />
      <If
        condition={data.customApl}
        then={() => (
          <Flex justify="center">
            <Badge pill theme="warning" mt={1}>Custom APL</Badge>
          </Flex>
        )}
      />

      <Divider m={1} />

      {_.map(_.filter(simcSettings, setting => setting.value), ({ key, value, description }) => (
        <div key={key}>
          <Flex>
            <Tooltip title={description}>
              <Text small>{key}</Text>
            </Tooltip>
            <Space auto />
            <If
              condition={typeof value === 'string'}
              then={() => (
                <Text small align="right">{value}</Text>
              )}
              else={() => (
                <Box>{value}</Box>
              )}
            />
          </Flex>
          <Divider m={1} />
        </div>
      ))}
      <div>
        <Flex>
          <Text small>
            Raw Files
          </Text>
          <Space auto />
          <FileMenu
            baseUrl={dataBaseUrl}
            hasFullJson={simbot.hasFullJson}
            hasCsv={simbot.hasCsv}
            hasOutput={!skippedHtml}
          />
        </Flex>
        <Divider m={1} />
      </div>
      <Flex justify="center">
        <Text small bold>
          <If
            condition={skippedHtml}
            then={() => (
              <span>Report disabled for large simulation</span>
            )}
            else={() => (
              <Link to={`/simbot/report/${simId}/simc`} href={`/simbot/report/${simId}/simc`}>
                Full HTML Report
              </Link>
            )}
          />
        </Text>
      </Flex>
    </Panel>
  );
};

SimDetails.propTypes = {
  report: PropTypes.shape({
    sim: PropTypes.object,
    simbot: PropTypes.object,
    version: PropTypes.string,
    build_date: PropTypes.string,
    build_time: PropTypes.string,
    git_rev: PropTypes.string,
    git_revision: PropTypes.string,
  }).isRequired,
  errorDetails: PropTypes.shape({
    error: PropTypes.number,
    percent: PropTypes.number,
  }).isRequired,
  simId: PropTypes.string.isRequired,
  expertMode: PropTypes.bool,
  unsupportedFightStyle: PropTypes.bool,
};

SimDetails.defaultProps = {
  expertMode: false,
  unsupportedFightStyle: false,
};

export default SimDetails;
